import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdOrdersOrderIdIdTicketsRollupSummary,
  GetV1CompaniesCompanyIdOrdersOrderIdIdTicketsRollupSummaryData,
  TicketSource,
} from '@treadinc/horizon-api-spec';
import { noop } from 'lodash-es';
import { createContext, useCallback, useMemo, useState } from 'react';

import { FetchReason } from '~constants/enums';
import { TicketsRollupSummary } from '~hooks/useTickets/models';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { TICKET_SUMMARY_QUERY_CONTEXT_PREFIX } from './constants';
import { makeTicketSummaryQueryContextKey } from './utils';

const deriveFilterQueryParams = (filters: TicketSummaryQueryFilters) => {
  const { sources } = filters;

  const query: GetV1CompaniesCompanyIdOrdersOrderIdIdTicketsRollupSummaryData['query'] = {
    'filter[sources]': sources?.length ? sources : undefined,
  };

  return query;
};

type TicketSummaryQueryFilters = {
  sources?: TicketSource[];
};

type TicketSummaryQueryContextType = {
  applyFilters: (filters: Partial<TicketSummaryQueryFilters>) => void;
  clear: () => void;
  data: TicketsRollupSummary[];
  fetchReason?: FetchReason;
  filters: TicketSummaryQueryFilters;
  isLoading: boolean;
  isFetching: boolean;
  refresh: () => void;
};

const TicketSummaryQueryContext = createContext<TicketSummaryQueryContextType>({
  applyFilters: noop,
  clear: noop,
  data: [],
  filters: {},
  isLoading: false,
  isFetching: false,
  refresh: noop,
});

function TicketSummaryQueryContextProvider({
  orderId,
  children,
}: React.PropsWithChildren<{ orderId: string }>) {
  const queryKey = makeTicketSummaryQueryContextKey(orderId);
  const queryClient = useQueryClient();

  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [filters, setFilters] = useState<TicketSummaryQueryFilters>({});

  const query = useQuery({
    queryKey: [queryKey, filters],
    queryFn: async ({ signal }) => {
      const queryParams = deriveFilterQueryParams(filters);

      const response = await getV1CompaniesCompanyIdOrdersOrderIdIdTicketsRollupSummary({
        path: { 'company-id': companyId, 'order-id': orderId },
        query: queryParams,
        signal,
      });

      return response.data.data.map(TicketsRollupSummary.parse);
    },
  });

  const data = query.data ?? [];

  const fetchReason = useMemo(() => {
    if (query.isFetching) {
      return FetchReason.INITIAL_FETCH;
    }
  }, [query.isFetching]);

  const clear = useCallback(() => {
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.some((key) => {
          const keyAsString = String(key);

          return (
            keyAsString.startsWith(TICKET_SUMMARY_QUERY_CONTEXT_PREFIX) ||
            keyAsString.startsWith(queryKey)
          );
        });
      },
    });
  }, [queryKey, queryClient]);

  const applyFilters = useCallback<TicketSummaryQueryContextType['applyFilters']>(
    (newFilters) => {
      clear();
      setFilters((state) => ({ ...state, ...newFilters }));
    },
    [clear],
  );

  const refresh = useCallback<TicketSummaryQueryContextType['refresh']>(() => {
    query.refetch();
  }, [query]);

  return (
    <TicketSummaryQueryContext.Provider
      value={{
        applyFilters,
        clear,
        data,
        fetchReason,
        filters,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        refresh,
      }}
    >
      {children}
    </TicketSummaryQueryContext.Provider>
  );
}

export default TicketSummaryQueryContext;
export { TicketSummaryQueryContextProvider };
